import { useEffect } from 'react'
import { ErrorBoundary } from '@mc/error-boundary'
import Experiment from '@mc/experiments-react'
import { usePrimaryCta } from '@mc/growth'
import Nav from '@mc/navigation'
import { paths } from '@mc/paths'
import { Product, trackExperimentViewed } from '@mc/track-event'
import CTA from 'components/CTA'
import { AdminLink } from 'components/Navigation/shared/AdminLink'
import { AtWorkLink } from 'components/Navigation/shared/AtWorkLink'
import { CertificatesLink } from 'components/Navigation/shared/CertificatesLink'
import { HideIfPathMatches } from 'components/Navigation/shared/HideIfPathMatches'
import { LoginMenu } from 'components/Navigation/shared/LoginMenu'
import MobileMenu from 'components/Navigation/shared/Mobile/Menu'
import { NavTitle } from 'components/Navigation/shared/NavTitle'
import { PlansLink } from 'components/Navigation/shared/PlansLink'
import { UserState } from 'components/UserState'
import {
  useIsMcCerts280VariantControl,
  useIsMcCerts280Variant1,
  useIsMcCerts280Variant2,
  useIsMcCerts280Variant3,
  MCCERTS_NAVIGATION_TESTING_PRE_PAYWALL_EXP_FLAG,
  MobileMenuVariant1,
  MobileMenuVariant2,
  MobileMenuVariant3,
  TopNavigation,
} from 'experiments/mccerts-280_v1'
import { useIsNewForBusinessPageEnabled } from '../enterprise/EnterpriseNavigation'
import { Browse } from './components/Browse'
import { GiftsLink } from './components/GiftsLink'
import { Search } from './components/Search'
import { SubcategoryBrowsingDropdownContent } from './components/SubcategoryBrowse'
import { trackSignUpCTAClick } from './events'
import { NavProvider, useSearchInputWidth } from './navContext'
import classes from './SubcategoryNavigation.module.scss'

const HIDE_SIGNUP_CTA_PATHS = [
  paths.checkout,
  paths.classRecommendations, // also matches quiz results as path prefix
  paths.bogo,
  paths.promotion,
]

export const SubcategoryNavigation = () => {
  const isMcCerts280VariantControlEnabled = useIsMcCerts280VariantControl()
  const isMcCerts280Variant1Enabled = useIsMcCerts280Variant1()
  const isMcCerts280Variant2Enabled = useIsMcCerts280Variant2()
  const isMcCerts280Variant3Enabled = useIsMcCerts280Variant3()
  const isNewForBusinessPageEnabled = useIsNewForBusinessPageEnabled()

  useEffect(() => {
    // Track the experiment viewed event for the control variation
    if (isMcCerts280VariantControlEnabled) {
      trackExperimentViewed(MCCERTS_NAVIGATION_TESTING_PRE_PAYWALL_EXP_FLAG)
    }
  }, [isMcCerts280VariantControlEnabled])

  return (
    <>
      {isMcCerts280Variant2Enabled && (
        <TopNavigation contained showAtWorkLink />
      )}
      {isMcCerts280Variant3Enabled && (
        <TopNavigation
          contained
          showOnCallLink
          showAtWorkLink
          product={Product.MASTERCLASS}
        />
      )}
      <Nav
        background='solid'
        className={`mc-page__header mc-theme-dark ${classes.navHeader}`}
        navChildClassName='flex-nowrap'
      >
        <NavTitle className={classes.compact} />
        <Nav.Left
          className={`flex-grow-1 flex-shrink-1 justify-content-sm-start justify-content-between ${classes.compact}`}
        >
          <ErrorBoundary>
            <NavBrowseComponent />
          </ErrorBoundary>
          <SignUpCTA nonMember className='d-lg-none mc-ml-sm-5' />
        </Nav.Left>
        <Nav.Right>
          {isMcCerts280Variant1Enabled && (
            <UserState nonMember>
              <CertificatesLink className='d-none d-lg-block' />
            </UserState>
          )}
          {!isMcCerts280Variant2Enabled && !isMcCerts280Variant3Enabled && (
            <UserState nonMember>
              <AtWorkLink className='d-none d-lg-block' />
            </UserState>
          )}
          <UserState nonMember>
            <HideIfPathMatches paths={[paths.checkout, paths.articles]}>
              <GiftsLink />
              <PlansLink />
            </HideIfPathMatches>
          </UserState>
          <UserState enterpriseAdmin>
            <AdminLink className='mc-ml-4' />
          </UserState>
          <LoginMenu>
            <Experiment
              name={MCCERTS_NAVIGATION_TESTING_PRE_PAYWALL_EXP_FLAG}
              control={() => <MobileMenu />}
              variant_1={() => <MobileMenuVariant1 />}
              variant_2={() => (
                <MobileMenuVariant2
                  newForBusinessPageEnabled={isNewForBusinessPageEnabled}
                />
              )}
              variant_3={() => (
                <MobileMenuVariant3
                  newForBusinessPageEnabled={isNewForBusinessPageEnabled}
                />
              )}
            />
          </LoginMenu>
          <SignUpCTA className='d-none d-lg-block mc-ml-4' />
        </Nav.Right>
      </Nav>
    </>
  )
}

const NavBrowseComponent = () => {
  const { searchInputRef, width } = useSearchInputWidth()

  return (
    <NavProvider width={width}>
      <Browse>
        <SubcategoryBrowsingDropdownContent />
      </Browse>
      <div ref={searchInputRef} className='d-none d-sm-flex flex-grow-1'>
        <Search />
      </div>
    </NavProvider>
  )
}

const SignUpCTA = ({
  className,
  nonMember,
}: {
  className: string
  nonMember?: boolean
}) => {
  const { ctaCopy } = usePrimaryCta()

  return (
    <UserState loggedOut nonMember={nonMember}>
      <HideIfPathMatches paths={HIDE_SIGNUP_CTA_PATHS}>
        <CTA
          className={className}
          onClick={trackSignUpCTAClick}
          trackProps={{
            action: `Header Nav Purchase CTA:${ctaCopy}`,
            level: 'primary',
            location: 'navigation',
          }}
        />
      </HideIfPathMatches>
    </UserState>
  )
}
